import { createTheme, type Shadows } from '@mui/material/styles';
import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';

/**
 * Infinity EV design guidelines v2.0 "Gallery", applied to the app:
 * white leads, Barlow only (400/500/600, nothing bolder), near-black text and main
 * button, graphite for quieter text, 1 px hairlines, no shadows, 8/12/16/24 px corners.
 * Colour is for status only (teal live/focus, green success, red fault), always with a word.
 *
 * Barlow is bundled with the app (not loaded from Google), so pages work offline-first
 * and nothing about the visitor is sent to a third party.
 */
export const colours = {
  white: '#FFFFFF',
  paleGrey: '#EEEFF2',
  nearBlack: '#010205',
  graphite: '#55585E',
  hairline: '#D8D8DB',
  night: '#0E0E12',
  softWhite: '#FBFCFF',
  coolGrey: '#A9ABB3',
  chargeMint: '#5CE1C6', // the charge motif's pins, the Manager role tag, and Charging (dot and word, chosen by Tony; 1.6:1 on white)
  signalTeal: '#0E6F73', // focus ring and live status dots
  successGreen: '#2E7250',
  availableGreen: '#16803C', // status: Available, Active and other good states. Brighter than success green; 5.0:1 on white
  faultRed: '#B42318',
  offlineOrange: '#CF7A04', // offline chargers, dot and word. 3.3:1 on white: below AA for text (4.5:1), chosen by Tony to stand apart from fault red
  productGrey: '#F5F6F8',
} as const;

export const radius = { button: 8, panel: 12, card: 16, large: 24 } as const;

const font = 'Barlow, "Arial Narrow", Arial, sans-serif';
const focusRing = { outline: `2px solid ${colours.signalTeal}`, outlineOffset: 2 };

/** The guidelines' phone / tablet / desktop widths. */
const breakpoints = { xs: 0, sm: 620, md: 1000, lg: 1280, xl: 1536 };
const phone = `@media (max-width:${breakpoints.sm - 0.05}px)`;

export const theme = createTheme({
  breakpoints: { values: breakpoints },
  palette: {
    mode: 'light',
    primary: { main: colours.nearBlack, contrastText: colours.white },
    secondary: { main: colours.graphite, contrastText: colours.white },
    error: { main: colours.faultRed },
    success: { main: colours.successGreen },
    info: { main: colours.signalTeal },
    warning: { main: colours.faultRed },
    text: { primary: colours.nearBlack, secondary: colours.graphite, disabled: '#8A8D93' },
    divider: colours.hairline,
    background: { default: colours.white, paper: colours.white },
    action: { hover: colours.paleGrey, selected: colours.paleGrey },
  },
  shape: { borderRadius: radius.button },
  shadows: Array(25).fill('none') as Shadows,
  typography: {
    fontFamily: font,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600, // the heaviest weight the guidelines allow
    // App pages are denser than the website, so the scale is a step smaller.
    h1: { fontSize: 40, lineHeight: '44px', fontWeight: 500, letterSpacing: '-0.01em', [phone]: { fontSize: 30, lineHeight: '34px' } },
    h2: { fontSize: 26, lineHeight: '32px', fontWeight: 500, letterSpacing: '-0.005em', [phone]: { fontSize: 22, lineHeight: '28px' } },
    h3: { fontSize: 18, lineHeight: '24px', fontWeight: 600 },
    h4: undefined,
    subtitle1: { fontSize: 19, lineHeight: '28px', fontWeight: 400, color: colours.graphite }, // P1 lead under a page headline
    body1: { fontSize: 16, lineHeight: '24px' },
    body2: { fontSize: 14, lineHeight: '21px' },
    caption: { fontSize: 13, lineHeight: '18px', fontWeight: 500, color: colours.graphite }, // small print and tags
    button: { fontSize: 16, lineHeight: '20px', fontWeight: 500, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { WebkitFontSmoothing: 'antialiased' },
        ':focus-visible': focusRing,
        a: { color: colours.nearBlack, fontWeight: 500, textUnderlineOffset: 3 },
        '.num': { fontVariantNumeric: 'tabular-nums' },
        b: { fontWeight: 600 },
        strong: { fontWeight: 600 },
      },
    },
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'outlined' },
      styleOverrides: {
        root: {
          minHeight: 44,
          borderRadius: radius.button,
          paddingInline: 20,
          whiteSpace: 'nowrap',
          '&.Mui-focusVisible': focusRing,
          variants: [
            { props: { variant: 'outlined', color: 'error' }, style: { color: colours.faultRed, borderColor: colours.hairline, '&:hover': { borderColor: colours.faultRed } } },
          ],
        },
        sizeSmall: { minHeight: 36, paddingInline: 14, fontSize: 15 },
        outlined: {
          borderColor: colours.hairline,
          color: colours.nearBlack,
          '&:hover': { borderColor: colours.nearBlack, backgroundColor: colours.white },
        },
        contained: { '&:hover': { backgroundColor: '#24262B' } },
        text: { paddingInline: 8, textDecoration: 'underline', textUnderlineOffset: 3, '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.button, color: colours.nearBlack, '&.Mui-focusVisible': focusRing },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.button,
          backgroundColor: colours.white,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colours.hairline },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colours.graphite },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colours.signalTeal, borderWidth: 2 },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: colours.faultRed },
        },
        input: { paddingBlock: 12 },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: colours.graphite, '&.Mui-focused': { color: colours.nearBlack } } } },
    MuiFormHelperText: { styleOverrides: { root: { marginInline: 2, fontSize: 14 } } },
    MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { outlined: { borderColor: colours.hairline, borderRadius: radius.card } } },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radius.card, border: `1px solid ${colours.hairline}` } },
    },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: 22, lineHeight: '28px', fontWeight: 500, padding: '24px 24px 8px' } } },
    MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 16px' } } },
    MuiDialogActions: { styleOverrides: { root: { padding: '8px 24px 24px', gap: 12, '& > :not(style) ~ :not(style)': { marginLeft: 0 } } } },
    MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(1,2,5,0.32)' } } },
    MuiMenu: { styleOverrides: { paper: { border: `1px solid ${colours.hairline}`, borderRadius: radius.panel, marginTop: 4 } } },
    MuiPopover: { styleOverrides: { paper: { border: `1px solid ${colours.hairline}`, borderRadius: radius.panel } } },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: colours.productGrey } } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${colours.hairline}`, fontSize: 16, paddingBlock: 14 },
        // Headers on pale grey, as in the Gallery guidelines; the panel's rounded corners clip it.
        head: { color: colours.graphite, backgroundColor: colours.paleGrey, fontWeight: 500, fontSize: 15, whiteSpace: 'nowrap' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontSize: 17, fontWeight: 400, color: colours.graphite, minHeight: 48, paddingInline: 18, '&.Mui-selected': { color: colours.nearBlack, fontWeight: 500 } },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { backgroundColor: colours.nearBlack, height: 2 }, root: { borderBottom: `1px solid ${colours.hairline}` } } },
    MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 500, fontSize: 13, backgroundColor: colours.paleGrey } } },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.panel,
          border: `1px solid ${colours.hairline}`,
          backgroundColor: colours.white,
          color: colours.nearBlack,
          alignItems: 'center',
          variants: [
            { props: { severity: 'error' }, style: { borderColor: colours.faultRed } },
            { props: { severity: 'success' }, style: { borderColor: colours.successGreen } },
          ],
        },
      },
    },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: colours.night, fontSize: 13, fontWeight: 500 } } },
    MuiLink: { defaultProps: { underline: 'always' }, styleOverrides: { root: { color: colours.nearBlack, fontWeight: 500 } } },
    MuiCheckbox: { styleOverrides: { root: { color: colours.graphite, '&.Mui-checked': { color: colours.nearBlack } } } },
  },
});
